import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/research", label: "Research & Papers", id: "nav-research-link" },
  { to: "/blog", label: "Blog & Articles", id: "nav-blog-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled || open ? "border-b border-line bg-white/95 backdrop-blur-md" : "bg-white"
        }`}
      >
        <div className="wrap flex h-16 items-center justify-between lg:h-20">
          <Link to="/" data-testid="nav-home-link" className="flex items-center">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? "text-blue" : "text-navy-soft hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="text-[12px] font-semibold uppercase tracking-[0.15em] text-navy-mute transition-colors duration-300 hover:text-blue"
            >
              Admin
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center border border-line text-navy md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-white px-8 md:hidden"
          >
            <nav className="flex flex-col gap-6">
              {[{ to: "/", label: "Home", id: "nav-mobile-home-link" }, ...LINKS.map((l) => ({ ...l, id: `nav-mobile-${l.id.replace("nav-", "")}` }))].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.id}
                    className={({ isActive }) =>
                      `font-serif text-4xl ${isActive ? "italic text-blue" : "text-navy"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link to="/admin" data-testid="nav-mobile-admin-link" className="text-xs font-semibold uppercase tracking-[0.25em] text-navy-mute">
                  Admin
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}